import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Sparkles } from 'lucide-react';

const COPY = {
  privacy: {
    title: 'Privacy Policy',
    updated: 'July 9, 2026',
    sections: [
      {
        heading: 'What we collect',
        body: 'Account information (name, email, password hash), billing metadata from payment providers, usage logs needed to operate the platform, and content you create in Snagcart (websites, leads, campaigns, AI outputs).',
      },
      {
        heading: 'How we use data',
        body: 'We use your data to provide the product, process payments, prevent abuse, improve reliability, and communicate service notices. We do not sell personal data.',
      },
      {
        heading: 'Processors',
        body: 'We rely on infrastructure and payment processors (for example MongoDB hosting, Stripe, PhonePe, email/SMS providers you connect). Their processing is governed by their own terms and our contracts with them.',
      },
      {
        heading: 'Retention & deletion',
        body: 'We retain account data while your account is active. You may request deletion by contacting support; some records may be retained where required for legal, tax, or fraud prevention.',
      },
      {
        heading: 'Contact',
        body: 'Privacy requests: use in-app Support or email the address published on your billing invoice / company site. This page is a working draft until counsel review.',
      },
    ],
  },
  terms: {
    title: 'Terms of Service',
    updated: 'July 9, 2026',
    sections: [
      {
        heading: 'Service',
        body: 'Snagcart provides website building, marketing, AI, and related tools on a subscription and credit basis. Features available depend on your plan and purchased modules.',
      },
      {
        heading: 'Acceptable use',
        body: 'You may not use the platform for illegal activity, spam, malware, infringement, or to attack third-party systems. We may suspend accounts that violate these terms.',
      },
      {
        heading: 'Your content',
        body: 'You retain ownership of content you upload or generate for your projects. You grant us a limited license to host and process that content to operate the service.',
      },
      {
        heading: 'Billing',
        body: 'Paid plans and credit packs are charged via configured payment providers. Fees are generally non-refundable except as described in the Refund Policy or required by law.',
      },
      {
        heading: 'Disclaimer',
        body: 'The service is provided “as is.” We do not guarantee uninterrupted availability or fitness for a particular purpose. Liability is limited to fees paid in the prior 12 months where permitted by law. This page is a working draft until counsel review.',
      },
    ],
  },
  refund: {
    title: 'Refund Policy',
    updated: 'July 9, 2026',
    sections: [
      {
        heading: 'Subscriptions',
        body: 'Monthly subscription fees are generally non-refundable once a billing period starts. If a charge was made in error, contact Support within 7 days with the invoice or payment ID.',
      },
      {
        heading: 'Credits & add-ons',
        body: 'Lead credits, domain wallet top-ups, and one-time add-ons are consumable and typically non-refundable after purchase, except for duplicate charges or failed fulfillment on our side.',
      },
      {
        heading: 'Chargebacks',
        body: 'Please contact Support before filing a chargeback so we can resolve billing issues quickly. Fraudulent chargebacks may result in account suspension.',
      },
      {
        heading: 'How to request',
        body: 'Open a Support ticket from your account or email billing support with your account email and payment reference. This page is a working draft until counsel review.',
      },
    ],
  },
  return: {
    title: 'Return Policy',
    updated: 'July 19, 2026',
    sections: [
      {
        heading: 'Digital service — no physical returns',
        body: 'Snagcart is a software-as-a-service (SaaS) platform. We do not sell physical goods. There are no product returns, exchanges, or RMA processes.',
      },
      {
        heading: 'Cancellations & billing',
        body: 'You may cancel a subscription from your account to stop future renewals. Fees already charged for the current period are handled under our Refund Policy, not as a “return.”',
      },
      {
        heading: 'Contact',
        body: 'For billing issues, open in-app Support or email the address on your invoice. This page is a working draft until counsel review.',
      },
    ],
  },
  shipping: {
    title: 'Shipping Policy',
    updated: 'July 19, 2026',
    sections: [
      {
        heading: 'No physical shipping',
        body: 'Snagcart delivers software and online services only. We do not ship parcels, inventory, or physical products. Access is provided digitally after successful signup or payment.',
      },
      {
        heading: 'Delivery of service',
        body: 'Paid plans, credits, and modules are activated in your Snagcart account online. Domain registrations (if purchased) are fulfilled electronically via the registrar.',
      },
      {
        heading: 'Contact',
        body: 'Questions about account access or digital delivery: use in-app Support. This page is a working draft until counsel review.',
      },
    ],
  },
};

export default function LegalPage({ kind = 'privacy' }) {
  const doc = COPY[kind] || COPY.privacy;
  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      <header className="border-b border-white/5 px-6 py-4 flex items-center justify-between">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white">
          <ArrowLeft className="w-4 h-4" />
          Back
        </Link>
        <div className="inline-flex items-center gap-2 font-semibold">
          <Sparkles className="w-4 h-4 text-violet-400" />
          Snagcart
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-6 py-12 space-y-8">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{doc.title}</h1>
          <p className="mt-2 text-sm text-zinc-500">Last updated {doc.updated}</p>
        </div>
        {doc.sections.map((section) => (
          <section key={section.heading} className="space-y-2">
            <h2 className="text-lg font-semibold text-white">{section.heading}</h2>
            <p className="text-sm leading-relaxed text-zinc-400">{section.body}</p>
          </section>
        ))}
        <p className="text-xs text-zinc-600 border-t border-white/5 pt-6">
          Related:{' '}
          <Link className="text-violet-400 hover:underline" to="/privacy">Privacy</Link>
          {' · '}
          <Link className="text-violet-400 hover:underline" to="/terms">Terms</Link>
          {' · '}
          <Link className="text-violet-400 hover:underline" to="/refund">Refund</Link>
          {' · '}
          <Link className="text-violet-400 hover:underline" to="/return-policy">Return</Link>
          {' · '}
          <Link className="text-violet-400 hover:underline" to="/shipping-policy">Shipping</Link>
        </p>
      </main>
    </div>
  );
}
